/** Building Permits — v1's Permits tab as a collapsible drawer section:
 * the lead's stamped permits in v1's table (Date / Permit # / Type / Status /
 * Description, tolerant field chains, AI pill), the county-portal link, and
 * the AI web search (same enrichment class as comps — the server stamps what
 * it finds onto the lead for next load). */
import { useMemo, useState } from "react";
import type { Lead } from "../../types";
import {
  PERMIT_MSG, countyOf, mergePermits, narrowPermits, permitHue, permitPortalUrl,
  type AiPermitResult, type PermitRec,
} from "./api";

export function PermitsSection({
  lead,
  fields,
  onAiSearch,
  startOpen = false,
}: {
  lead: Lead;
  fields: Record<string, unknown> | undefined;
  onAiSearch: (address: string, county: string, leadId: string) => Promise<AiPermitResult>;
  startOpen?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [found, setFound] = useState<PermitRec[]>([]);
  const [checkedNow, setCheckedNow] = useState(false);

  const county = countyOf(lead.county, lead.address);
  const stamped = useMemo(() => narrowPermits(lead.permits), [lead.permits]);
  const permits = useMemo(() => mergePermits(stamped, found), [stamped, found]);
  const permitUrl = typeof fields?.["permitUrl"] === "string" ? (fields["permitUrl"] as string) : lead.permitUrl;
  const portal = permitPortalUrl(permitUrl, county, lead.address);
  const siteLink = permits.find((p) => p.website)?.website ?? null;

  const runAi = async () => {
    if (busy) return;
    setBusy(true);
    setNote("Checking county records first…");
    try {
      const r = await onAiSearch(lead.address, county, lead.id);
      setCheckedNow(true);
      if (!r.ok) setNote(PERMIT_MSG.aiFailed(r.detail ?? ""));
      else if (r.skipped) {
        // county-first (Tom 2026-08-13): county rows exist -> AI never ran
        setFound((prev) => mergePermits(prev, r.permits));
        setNote(`✓ ${r.count} county permit${r.count === 1 ? "" : "s"} on file — AI not needed.`);
      } else {
        const prefix = r.alert
          ? "⚠ " + r.alert + " "
          : r.countyStatus === "ok-empty"
            ? "County path healthy but no county permits on file — AI ran as backup. "
            : "";
        if (!r.permits.length) setNote(prefix + PERMIT_MSG.aiNone(r.website));
        else {
          setFound((prev) => mergePermits(prev, r.permits));
          setNote(prefix + PERMIT_MSG.aiFound(r.count));
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const hueClass = { done: "cmp-sold", open: "cmp-pending", dead: "permit-dead", unknown: "mail-log" } as const;

  return (
    <div className="detailsec open">
      <div className="sechead static">
        <span>🔨 Permits</span>
        <span className="sub">
          {permits.length ? `${permits.length} on record · ` : ""}
        </span>
      </div>
        <div className="secbody permitsec">
          <div className="mailhead">
            <span className="sub">
              {permits.length ? "County records first; AI web search as backup." : "County scraper first — or run the AI web search."}
            </span>
            <div className="mailacts">
              <a className="btn slim" href={portal} target="_blank" rel="noreferrer">County portal ↗</a>
              <button className={"btn slim" + (permits.length ? "" : " primary")} disabled={busy} onClick={() => void runAi()}>
                {busy ? "Searching…" : "✦ AI permit search"}
              </button>
            </div>
          </div>
          {note ? <div className="cmpnote">{note}</div> : null}
          {permits.length ? (
            <>
              {siteLink ? (
                <a className="dlink permitsite" href={siteLink} target="_blank" rel="noreferrer">permit site →</a>
              ) : null}
              <div className="permittable">
                <div className="permitrow permithead">
                  <span>Date</span><span>Permit #</span><span>Type / Work</span><span>Status</span><span>Description</span><span>Source</span>
                </div>
                {permits.map((p, i) => (
                  <div key={p.num + i} className="permitrow">
                    <span className="pdate">{p.date || "—"}</span>
                    <span className="pnum">
                      {p.link ? (
                        <a href={p.link} target="_blank" rel="noreferrer">{p.num || "view"}</a>
                      ) : (
                        p.num || "—"
                      )}
                    </span>
                    <span>
                      {p.type || "—"}
                      {p.work && p.work !== p.type ? <em className="pwork">{p.work}</em> : null}
                    </span>
                    <span>
                      <span className={"cmppill " + hueClass[permitHue(p.status)]}>{p.status || "—"}</span>
                      {p.ai ? <span className="cmppill permit-ai"> AI</span> : null}
                    </span>
                    <span className="pdesc">{p.desc && p.desc !== p.type ? p.desc : "—"}{p.amount ? <em className="pwork"> · {p.amount}</em> : null}</span>
                    {/* Tom 2026-08-13 (2586 Redwood Cir): "I need to know from
                        WHERE — it should be written near every permit." The
                        domain each record came from: the direct record link
                        first, else the search's main site, else the county
                        scraper. Linked when we have a URL. */}
                    <span className="psrc">
                      {(() => {
                        const url = p.link || p.website || "";
                        const dom = url ? url.replace(/^https?:\/\/(www\.)?/, "").split(/[/?#]/)[0] : "";
                        if (url && dom) return <a className="dlink" href={url} target="_blank" rel="noreferrer" title={url}>{dom}</a>;
                        return <span className="sub dim">{p.ai ? "AI search" : "county records"}</span>;
                      })()}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="arvempty">
              <div className="sub">No permits yet</div>
              <span className="sub dim">{lead.permitsChecked || checkedNow ? PERMIT_MSG.emptyChecked : PERMIT_MSG.emptyNew}</span>
            </div>
          )}
        </div>
    </div>
  );
}
